import type { FormikHelpers } from "formik";
import { Formik, getIn, useFormikContext } from "formik";
import { useEffect, useMemo, useState } from "react";
import GenericTierTwo from "../GenericTierTwo";
import GenericModule from "../GenericModule";

import {
	getSchemas,
	validationSchema,
	initialValues,
	t2InitialValues,
} from "./smallFisheries";
import type { ModuleFull } from "./smallFisheriesTypes";
import {
	useAppSelector,
	useThreadsProcessor,
} from "@app/hooks";
import { selectCurrentProject } from "@features/project/projectSlice";
import {
	useContextAwareGetSmallFisheriesDefaults,
	useContextAwareGetSmallFisheries,
	useUpdateSmallFisheriesMutation,
	useGetSmallFishGearCategoryQuery,
} from "@hooks/index";
import useModuleHook from "../useModuleHook";
import { BEModulesClassNames, FEModules } from "@utils/moduleList";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import { InputGroup, ModuleSchema } from "@typedefs/modulesInterfaces";
import useErrorMessage from "@utils/useErrorMessage";

const SmallFisheriesModule = () => {
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const [schemaUpdated, setSchemaUpdated] = useState<ModuleSchema | null>(null);

	const id = moduleList.find(({ id }) => id === FEModules.SmallFisheries)?.uniqueId;

	const {
		data: smallFishData,
		isLoading,
		error,
		isError,
	} = useContextAwareGetSmallFisheries(id ?? 0, { refetchOnMountOrArgChange: true });

	const { data: defaults, isError: isDefaultsError, error: defaultsError, isLoading: isLoadingDefaults } = useContextAwareGetSmallFisheriesDefaults(id ?? 0, { skip: !id });
	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError: isDefaultsError, error: defaultsError });

	const [
		updateSmallFishery,
		{ error: updateError, isError: isUpdateError, isLoading: isLoadingUpdate },
	] = useUpdateSmallFisheriesMutation();

	const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);

	//formik initial values
	const [initValues, setInitValues] = useState<ModuleFull>({
		module: initialValues,
		tiertwo: t2InitialValues,
	});

	const { tabsOpen, errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isLoadingDefaults],
		isMutateError: isUpdateError,
		mutateError: updateError,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error });
	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();
	useEffect(() => {
		if (smallFishData) {
			setInitValues({
				module: { ...smallFishData.module, ...defaults },
				tiertwo: { ...smallFishData.tiertwo, ...defaults },
			});
			processModuleThreads({
				module: smallFishData.module,
			})
		}

	}, [smallFishData, defaults, processModuleThreads]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		const module = { ...values.module };
		const payload = {
			data: { ...values, module },
			activityId: activeActivityId ?? 0,
		};

		try {
			values.module.id && (await updateSmallFishery(payload).unwrap());
			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Formik
			validationSchema={validationSchema}
			initialValues={initValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<GenericModule
					title="project.small_fisheries"
					moduleSchema={schemaUpdated ?? moduleSchema}
					t2Schema={t2Schema}
					beModuleType={BEModulesClassNames.SmallFisheries}
					isLoading={isLoadingUpdate}
					footerError={errorMsg}
					blockingError={blockingError}
					isError={isUpdateError}
				>
					{
						() => (
							<HandleSchemaUpdate updateSchema={setSchemaUpdated} baseSchema={moduleSchema} />
						)
					}
				</GenericModule>
				{!tabsOpen ? (
					<GenericTierTwo
						title="project.small_fisheries"
						t2Schema={t2Schema}
						tabsOpen={tabsOpen}
						warningMessage={defaultsErrorMsg}
					/>
				) : null}
			</>
		</Formik>
	);
};

export default SmallFisheriesModule;

/**
 * This component is used to update the schema based on the fishery type.
 * It is used to update the gear type dropdown options based on the fishery type.
 * It is also used to reset the gear type values if the option selected is not in the dropdown options anymore.
 */

const HandleSchemaUpdate = ({ updateSchema, baseSchema }: { updateSchema: (schema: ModuleSchema) => void, baseSchema: ModuleSchema }) => {
	const { values, setFieldValue } = useFormikContext<ModuleFull>();
	const fisheryType: number | undefined = useMemo(() => getIn(values, "module.fishery_type"), [values]);

	const { data: gearTypes } = useGetSmallFishGearCategoryQuery({ fishery_types: fisheryType }, { skip: !fisheryType });

	// Update the schema based on the fishery type.
	useEffect(() => {
		if (gearTypes) {
			const smallScaleFisheriesSubmodule = baseSchema.mandatorySubmodules
				.find((submodule) => submodule.name === "module.small_scale_fisheries");

			if (smallScaleFisheriesSubmodule) {
				const updatedInputGroups = smallScaleFisheriesSubmodule.inputGroups.map((inputGroup: InputGroup) => {
					if (inputGroup.inputName === "module.gear_type") {
						return {
							...inputGroup,
							startProps: { dropdownOptions: gearTypes },
							withProps: { dropdownOptions: gearTypes },
							withoutProps: { dropdownOptions: gearTypes },
						};
					}
					return inputGroup;
				});

				const updatedSubmodule = {
					...smallScaleFisheriesSubmodule,
					inputGroups: updatedInputGroups,
				};

				const newSchema = {
					...baseSchema,
					mandatorySubmodules: baseSchema.mandatorySubmodules.map((submodule) =>
						submodule.name === "module.small_scale_fisheries" ? updatedSubmodule : submodule
					),
				};

				updateSchema(newSchema);
			}
		}
	}, [gearTypes, updateSchema, baseSchema]);

	// Reset the gear type values if the option selected is not in the dropdown options anymore.
	useEffect(() => {
		if (gearTypes) {
			const gearCategoryStart = getIn(values, "module.gear_type_start");
			const gearCategoryWith = getIn(values, "module.gear_type_w");
			const gearCategoryWithout = getIn(values, "module.gear_type_wo");

			const isStartInGearTypes = gearTypes.some((gearType) => gearType.id === gearCategoryStart);
			const isWithInGearTypes = gearTypes.some((gearType) => gearType.id === gearCategoryWith);
			const isWithoutInGearTypes = gearTypes.some((gearType) => gearType.id === gearCategoryWithout);

			if (!isStartInGearTypes) {
				setFieldValue("module.gear_type_start", null);
			}
			if (!isWithInGearTypes) {
				setFieldValue("module.gear_type_w", null);
			}
			if (!isWithoutInGearTypes) {
				setFieldValue("module.gear_type_wo", null);
			}
		}
	}, [gearTypes, setFieldValue, values]);

	return null;
}